/**
 * Lingue dell'interfaccia: italiano e inglese (i18next). I testi stanno in testi/<area>.ts, l'inglese con la stessa forma
 * dell'italiano (lo controlla tsc); le etichette del dominio (stati, tipi, ruoli, liste) e i messaggi del backend vengono da
 * @qa/shared (`dominio.*`, `errori.*`). I testi scritti dagli utenti e i nomi delle voci di lista non si traducono.
 * I testi dell'aiuto in linea (@brandart/help-kit, STD-6) li aggiunge QA26-F5 accanto a questi.
 */
import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import {
  ESITO_VERIFICA_LABEL,
  FASE_NC_LABEL,
  LISTA_LABEL,
  MESSAGGI,
  RUOLO_DESCRIZIONE,
  RUOLO_LABEL,
  RUOLO_PORTALE_DESCRIZIONE,
  RUOLO_PORTALE_LABEL,
  SEZIONE_RUOLI_LABEL,
  STATO_CLAIM_LABEL,
  STATO_PROPOSTA_LABEL,
  STATO_AZIONE_LABEL,
  STATO_SEGNALAZIONE_LABEL,
  TIPO_AZIONE_LABEL,
  TIPO_CLASSIFICAZIONE_LABEL,
  TIPO_NC_LABEL,
  TIPO_PROPOSTA_LABEL,
  VOCE_COSTO_LABEL,
  type Lingua,
  type Testo,
} from "@qa/shared";
import * as comune from "./testi/comune";
import * as redflag from "./testi/redflag";
import * as nc from "./testi/nc";
import * as claim from "./testi/claim";
import * as proposte from "./testi/proposte";
import * as admin from "./testi/admin";
import { linguaIniziale, localeDi } from "./lingua";

const scegli = <K extends string>(m: Record<K, Testo>, l: Lingua) => Object.fromEntries(Object.entries(m).map(([k, v]) => [k, (v as Testo)[l]])) as Record<K, string>;

/** Chiave i18n di un ruolo del portale: `nc.ims` → `nc_ims` (`t(\`dominio.ruoloPortale.${chiaveRuolo(r)}\`)`). */
export const chiaveRuolo = (r: string) => r.replace(".", "_");
const perChiave = (m: Record<string, Testo>) => Object.fromEntries(Object.entries(m).map(([k, v]) => [chiaveRuolo(k), v]));

const dominio = (l: Lingua) => ({
  ruolo: scegli(RUOLO_LABEL, l),
  ruoloDescrizione: scegli(RUOLO_DESCRIZIONE, l),
  // ruoli del portale (qa.admin, redflag.*, nc.*): il punto separa le chiavi di i18next, qui diventa «_» (chiaveRuolo)
  ruoloPortale: scegli(perChiave(RUOLO_PORTALE_LABEL), l),
  ruoloPortaleDescrizione: scegli(perChiave(RUOLO_PORTALE_DESCRIZIONE), l),
  sezioneRuoli: scegli(SEZIONE_RUOLI_LABEL, l),
  statoRf: scegli(STATO_SEGNALAZIONE_LABEL, l),
  tipoClass: scegli(TIPO_CLASSIFICAZIONE_LABEL, l),
  tipoAzione: scegli(TIPO_AZIONE_LABEL, l),
  statoAzione: scegli(STATO_AZIONE_LABEL, l),
  tipoNc: scegli(TIPO_NC_LABEL, l),
  faseNc: scegli(FASE_NC_LABEL, l),
  esito: scegli(ESITO_VERIFICA_LABEL, l),
  voceCosto: scegli(VOCE_COSTO_LABEL, l),
  statoClaim: scegli(STATO_CLAIM_LABEL, l),
  tipoProposta: scegli(TIPO_PROPOSTA_LABEL, l),
  statoProposta: scegli(STATO_PROPOSTA_LABEL, l),
  lista: scegli(LISTA_LABEL, l),
});

const app = (l: Lingua) => {
  const pick = <T,>(m: { it: T; en: T }) => (l === "it" ? m.it : m.en) as typeof m.it;
  return {
    ...pick(comune),
    redflag: pick(redflag),
    nc: pick(nc),
    claim: pick(claim),
    proposte: pick(proposte),
    admin: pick(admin),
    dominio: dominio(l),
    errori: Object.fromEntries(Object.entries(MESSAGGI).map(([k, v]) => [k, v[l]])) as Record<keyof typeof MESSAGGI, string>,
  };
};

export type Testi = ReturnType<typeof app>;

void i18n.use(initReactI18next).init({
  resources: { it: { translation: app("it") }, en: { translation: app("en") } },
  lng: linguaIniziale(),
  fallbackLng: "it",
  interpolation: { escapeValue: false },
  returnNull: false,
});

const aggiornaDocumento = (l: string) => {
  if (typeof document !== "undefined") document.documentElement.lang = l;
};
aggiornaDocumento(i18n.language);
i18n.on("languageChanged", aggiornaDocumento);

export const linguaCorrente = (): Lingua => (i18n.language === "it" ? "it" : "en");
/** Locale Intl della lingua corrente (numeri, valute, date). */
export const locale = () => localeDi(linguaCorrente());

export default i18n;
